/**
 * Utilities for parsing and validating chart JSON specifications from chatbot messages
 */

/**
 * Detects and extracts chart JSON blocks from markdown content
 * Supports two formats:
 * 1. Markdown code fence: ```chart\n{json}\n```
 * 2. XML-style tags: <chart-json>{json}</chart-json>
 * 
 * @param {string} content - The message content to parse
 * @returns {Array<Object>} Array of chart specifications with their positions
 */
export const extractCharts = (content) => {
  if (!content || typeof content !== 'string') {
    return [];
  }

  const charts = [];
  
  // Pattern 1: Markdown code fence with 'chart' language tag
  // Matches: ```chart\n{...}\n``` (handles optional \r before \n for CRLF)
  const fenceRegex = /```chart[^\n]*\r?\n([\s\S]*?)\r?\n\s*```/g;
  let match;
  
  const hasFence = content.includes('```chart');
  if (hasFence) {
    console.debug('[ChartUtils] Found ```chart fence, attempting parse. Content length:', content.length);
  }

  while ((match = fenceRegex.exec(content)) !== null) {
    try {
      const jsonStr = match[1].trim();
      console.debug('[ChartUtils] Extracted JSON string:', jsonStr.substring(0, 100) + '...');
      const chartSpec = JSON.parse(jsonStr);
      console.debug('[ChartUtils] Parsed chart spec:', chartSpec.type, '— data points:', chartSpec.data?.length);
      charts.push({
        json: chartSpec,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
        rawMatch: match[0]
      });
    } catch (error) {
      console.warn('[ChartUtils] Failed to parse chart JSON from code fence:', error);
      console.warn('[ChartUtils] Raw match content:', match[1]?.substring(0, 200));
    }
  }

  if (hasFence && charts.length === 0) {
    console.warn('[ChartUtils] Found ```chart fence but regex did not match. First 300 chars around fence:',
      content.substring(Math.max(0, content.indexOf('```chart') - 20), content.indexOf('```chart') + 300)
    );
  }
  
  // Pattern 2: XML-style tags
  // Matches: <chart-json>{...}</chart-json>
  const xmlRegex = /<chart-json>([\s\S]*?)<\/chart-json>/g;
  
  while ((match = xmlRegex.exec(content)) !== null) {
    try {
      const jsonStr = match[1].trim();
      const chartSpec = JSON.parse(jsonStr);
      charts.push({
        json: chartSpec,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
        rawMatch: match[0]
      });
    } catch (error) {
      console.warn('Failed to parse chart JSON from XML tags:', error);
    }
  }
  
  // Sort by position in the document
  charts.sort((a, b) => a.startIndex - b.startIndex);
  
  return charts;
};

/**
 * Validates a chart specification object
 * 
 * @param {Object} chartSpec - The chart specification to validate
 * @returns {Object} { valid: boolean, errors: Array<string> }
 */
export const validateChartSpec = (chartSpec) => {
  const errors = [];
  
  if (!chartSpec || typeof chartSpec !== 'object') {
    errors.push('Chart specification must be an object');
    return { valid: false, errors };
  }
  
  // Required fields
  if (!chartSpec.type) {
    errors.push('Chart type is required');
  } else {
    const validTypes = ['line', 'bar', 'pie', 'area', 'scatter'];
    if (!validTypes.includes(chartSpec.type.toLowerCase())) {
      errors.push(`Invalid chart type: ${chartSpec.type}. Must be one of: ${validTypes.join(', ')}`);
    }
  }
  
  if (!chartSpec.data || !Array.isArray(chartSpec.data)) {
    errors.push('Chart data must be an array');
  } else if (chartSpec.data.length === 0) {
    errors.push('Chart data cannot be empty');
  }
  
  // Type-specific validations
  if (chartSpec.type === 'pie') {
    // Pie charts need specific data format
    if (chartSpec.data && chartSpec.data.length > 0) {
      const firstItem = chartSpec.data[0];
      if (!('name' in firstItem) || !('value' in firstItem)) {
        errors.push('Pie chart data items must have "name" and "value" properties');
      }
    }
  } else {
    // Line, bar, area, scatter charts
    if (!chartSpec.xAxis) {
      errors.push('xAxis field is required for line/bar/area/scatter charts');
    }
    
    if (!chartSpec.series || !Array.isArray(chartSpec.series) || chartSpec.series.length === 0) {
      errors.push('At least one series is required for line/bar/area/scatter charts');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Removes chart blocks from content, leaving the rest of the markdown intact
 * 
 * @param {string} content - Original content with chart blocks
 * @param {Array<Object>} charts - Array of chart objects from extractCharts
 * @returns {string} Content with chart blocks removed
 */
export const removeChartBlocks = (content, charts) => {
  if (!charts || charts.length === 0) {
    return content;
  }
  
  // Sort charts in reverse order to remove from end to start
  // This preserves indices as we remove chunks
  const sortedCharts = [...charts].sort((a, b) => b.startIndex - a.startIndex);
  
  let result = content;
  for (const chart of sortedCharts) {
    result = result.slice(0, chart.startIndex) + result.slice(chart.endIndex);
  }
  
  return result;
};

/**
 * Detects whether there is an in-progress (incomplete) chart block in the content.
 * An in-progress block has an opening ```chart fence but no closing fence yet.
 *
 * @param {string} content
 * @returns {boolean}
 */
export const hasPartialChart = (content) => {
  if (!content || typeof content !== 'string') return false;

  // Count opening ```chart fences
  const openMatches = content.match(/```chart/g);
  if (!openMatches) return false;

  // Count closing fences that follow a ```chart opener
  const closingMatches = content.match(/```chart[\s\S]*?```/g);
  const openCount = openMatches.length;
  const closedCount = closingMatches ? closingMatches.length : 0;

  return openCount > closedCount;
};

/**
 * Strips an in-progress (incomplete) chart block from the content so raw JSON
 * is not shown while streaming.
 *
 * @param {string} content
 * @returns {string}
 */
export const stripPartialChart = (content) => {
  if (!content || typeof content !== 'string') return content;
  // Remove everything from the last unclosed ```chart to the end of the string
  return content.replace(/```chart[\s\S]*$/, '').trimEnd();
};

/**
 * Processes message content to extract charts and clean text
 * 
 * @param {string} content - The message content
 * @returns {Object} { text: string, charts: Array<Object>, isGeneratingChart: boolean }
 */
export const processMessageContent = (content) => {
  const charts = extractCharts(content);
  const generating = hasPartialChart(content);
  const textWithCharsRemoved = removeChartBlocks(content, charts);
  const text = generating
    ? stripPartialChart(textWithCharsRemoved)
    : textWithCharsRemoved;
  
  return {
    text: text.trim(),
    charts: charts.map(c => c.json),
    isGeneratingChart: generating,
  };
};
